import { PARAMS, ROUTES } from "@src/common/constants";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type {
	DemoWalkthroughState,
	DemoWalkthroughStep,
} from "./onboarding-demo-overlay/types";

export const ONBOARDING_DEMO_STORAGE_KEY = "illumi-onboarding-demo";

type OnboardingDemoSessionState = {
	mainPrompt: string;
	supportingNotes: string[];
	aiSuggestedSupportingNote: string;
	aiSuggestedSupportingNoteDismissed: boolean;
	hasConsumedInitialGeneration: boolean;
	hasConsumedFollowUpGeneration: boolean;
	promptCardShapeId: string | null;
	promptCardEntityId: string | null;
	preprintShapeId: string | null;
	aiCollaboratorCardEntityId: string | null;
	followUpPromptCardEntityId: string | null;
	isBoardViewOnly: boolean;
	areCardActionsLocked: boolean;
};

const DEFAULT_SESSION_STATE: OnboardingDemoSessionState = {
	mainPrompt: "",
	supportingNotes: ["", ""],
	aiSuggestedSupportingNote: "",
	aiSuggestedSupportingNoteDismissed: false,
	hasConsumedInitialGeneration: false,
	hasConsumedFollowUpGeneration: false,
	promptCardShapeId: null,
	promptCardEntityId: null,
	preprintShapeId: null,
	aiCollaboratorCardEntityId: null,
	followUpPromptCardEntityId: null,
	isBoardViewOnly: false,
	areCardActionsLocked: false,
};

export type DemoRegistrationPersist = {
	demoSessionId: string | undefined;
	demoWhiteboardId: string | undefined;
};

type OnboardingDemoPersistedState = OnboardingDemoSessionState &
	DemoRegistrationPersist & {
		persistedDemoWhiteboardId: string | null;
		walkthroughStep: DemoWalkthroughStep | null;
		walkthroughState: DemoWalkthroughState | null;
	};

type OnboardingDemoStore = OnboardingDemoPersistedState & {
	enabled: boolean;
	initialize: (params: { enabled: boolean; whiteboardId: string }) => void;
	setMainPrompt: (mainPrompt: string) => void;
	setSupportingNote: (index: number, value: string) => void;
	setAiSuggestedSupportingNote: (value: string) => void;
	dismissAiSuggestedSupportingNote: () => void;
	restoreAiSuggestedSupportingNote: () => void;
	markInitialGeneration: (params: {
		promptCardShapeId: string;
		promptCardEntityId: string;
		preprintShapeId: string | null;
		aiCollaboratorCardEntityId: string | null;
	}) => void;
	registerFollowUpPrompt: (promptCardEntityId: string) => void;
	markFollowUpGenerationConsumed: () => void;
	markBoardViewOnly: () => void;
	setCardActionsLocked: (locked: boolean) => void;
	setWalkthroughStep: (step: DemoWalkthroughStep | null) => void;
	setWalkthroughState: (
		state:
			| DemoWalkthroughState
			| null
			| ((
					previous: DemoWalkthroughState | null,
			  ) => DemoWalkthroughState | null),
	) => void;
	saveDemoRegistration: (context: DemoRegistrationPersist) => void;
	clearRegistrationContext: () => void;
	reset: () => void;
};

const initialPersistedSlice: OnboardingDemoPersistedState = {
	...DEFAULT_SESSION_STATE,
	persistedDemoWhiteboardId: null,
	demoSessionId: undefined,
	demoWhiteboardId: undefined,
	walkthroughStep: null,
	walkthroughState: null,
};

function buildSessionResetState(): Pick<
	OnboardingDemoPersistedState,
	| keyof OnboardingDemoSessionState
	| "persistedDemoWhiteboardId"
	| "walkthroughStep"
	| "walkthroughState"
> {
	return {
		...DEFAULT_SESSION_STATE,
		persistedDemoWhiteboardId: null,
		walkthroughStep: null,
		walkthroughState: null,
	};
}

export const useOnboardingDemoStore = create<OnboardingDemoStore>()(
	persist(
		(set, get) => ({
			enabled: false,
			...initialPersistedSlice,
			initialize: ({ enabled, whiteboardId }) => {
				if (!enabled) {
					set({
						enabled: false,
						...buildSessionResetState(),
					});
					return;
				}

				const previous = get();
				if (previous.persistedDemoWhiteboardId === whiteboardId) {
					set({ enabled: true });
					return;
				}

				set({
					enabled: true,
					...DEFAULT_SESSION_STATE,
					persistedDemoWhiteboardId: whiteboardId,
					walkthroughStep: null,
					walkthroughState: null,
					demoSessionId: previous.demoSessionId,
					demoWhiteboardId: previous.demoWhiteboardId,
				});
			},
			setMainPrompt: (mainPrompt) => {
				set((state) => {
					if (!state.enabled) return state;
					return { ...state, mainPrompt };
				});
			},
			setSupportingNote: (index, value) => {
				set((state) => {
					if (!state.enabled) return state;
					const nextSupportingNotes = [...state.supportingNotes];
					nextSupportingNotes[index] = value;
					return {
						...state,
						supportingNotes: nextSupportingNotes,
					};
				});
			},
			setAiSuggestedSupportingNote: (value) => {
				set((state) => {
					if (!state.enabled) return state;
					return {
						...state,
						aiSuggestedSupportingNote: value,
						aiSuggestedSupportingNoteDismissed: false,
					};
				});
			},
			dismissAiSuggestedSupportingNote: () => {
				set((state) => {
					if (!state.enabled) return state;
					return {
						...state,
						aiSuggestedSupportingNoteDismissed: true,
					};
				});
			},
			restoreAiSuggestedSupportingNote: () => {
				set((state) => {
					if (!state.enabled) return state;
					return {
						...state,
						aiSuggestedSupportingNoteDismissed: false,
					};
				});
			},
			markInitialGeneration: ({
				promptCardShapeId,
				promptCardEntityId,
				preprintShapeId,
				aiCollaboratorCardEntityId,
			}) => {
				set((state) => {
					if (!state.enabled) return state;
					return {
						...state,
						hasConsumedInitialGeneration: true,
						promptCardShapeId,
						promptCardEntityId,
						preprintShapeId,
						aiCollaboratorCardEntityId,
					};
				});
			},
			registerFollowUpPrompt: (promptCardEntityId) => {
				set((state) => {
					if (!state.enabled) return state;
					return {
						...state,
						followUpPromptCardEntityId: promptCardEntityId,
					};
				});
			},
			markFollowUpGenerationConsumed: () => {
				set((state) => {
					if (!state.enabled) return state;
					return {
						...state,
						hasConsumedFollowUpGeneration: true,
					};
				});
			},
			markBoardViewOnly: () => {
				set((state) => {
					if (!state.enabled) return state;
					return {
						...state,
						isBoardViewOnly: true,
						areCardActionsLocked: true,
					};
				});
			},
			setCardActionsLocked: (locked) => {
				set((state) => {
					if (!state.enabled) return state;
					return {
						...state,
						areCardActionsLocked: locked,
					};
				});
			},
			setWalkthroughStep: (walkthroughStep) => {
				set((state) => {
					if (!state.enabled) return state;
					return { ...state, walkthroughStep };
				});
			},
			setWalkthroughState: (next) => {
				set((state) => {
					if (!state.enabled) return state;
					const walkthroughState =
						typeof next === "function" ? next(state.walkthroughState) : next;
					return { ...state, walkthroughState };
				});
			},
			saveDemoRegistration: (context) => {
				set((state) => ({
					...state,
					demoSessionId: context.demoSessionId,
					demoWhiteboardId: context.demoWhiteboardId,
				}));
			},
			clearRegistrationContext: () => {
				set((state) => ({
					...state,
					demoSessionId: undefined,
					demoWhiteboardId: undefined,
				}));
			},
			reset: () => {
				set((state) => ({
					enabled: state.enabled,
					...DEFAULT_SESSION_STATE,
					persistedDemoWhiteboardId: state.persistedDemoWhiteboardId,
					walkthroughStep: null,
					walkthroughState: null,
					demoSessionId: state.demoSessionId,
					demoWhiteboardId: state.demoWhiteboardId,
				}));
			},
		}),
		{
			name: ONBOARDING_DEMO_STORAGE_KEY,
			storage: createJSONStorage(() => sessionStorage),
			partialize: (state) => ({
				persistedDemoWhiteboardId: state.persistedDemoWhiteboardId,
				demoSessionId: state.demoSessionId,
				demoWhiteboardId: state.demoWhiteboardId,
				mainPrompt: state.mainPrompt,
				supportingNotes: state.supportingNotes,
				aiSuggestedSupportingNote: state.aiSuggestedSupportingNote,
				aiSuggestedSupportingNoteDismissed:
					state.aiSuggestedSupportingNoteDismissed,
				hasConsumedInitialGeneration: state.hasConsumedInitialGeneration,
				hasConsumedFollowUpGeneration: state.hasConsumedFollowUpGeneration,
				promptCardShapeId: state.promptCardShapeId,
				promptCardEntityId: state.promptCardEntityId,
				preprintShapeId: state.preprintShapeId,
				aiCollaboratorCardEntityId: state.aiCollaboratorCardEntityId,
				followUpPromptCardEntityId: state.followUpPromptCardEntityId,
				isBoardViewOnly: state.isBoardViewOnly,
				areCardActionsLocked: state.areCardActionsLocked,
				walkthroughStep: state.walkthroughStep,
				walkthroughState: state.walkthroughState,
			}),
		},
	),
);

export type DemoRegistrationContext = {
	demoSessionId?: string;
	demoWhiteboardId?: string;
};

export function extractDemoRegistrationContext(
	url: URL,
): DemoRegistrationContext {
	const demoSessionId = url.searchParams.get(PARAMS.demoSessionId) ?? undefined;
	const whiteboardPrefix = `${ROUTES.whiteboards}/`;
	const demoWhiteboardId = url.pathname.startsWith(whiteboardPrefix)
		? decodeURIComponent(url.pathname.slice(whiteboardPrefix.length)) ||
			undefined
		: undefined;

	return {
		demoSessionId,
		demoWhiteboardId,
	};
}

export function saveDemoRegistrationContext(
	context: DemoRegistrationContext,
): void {
	if (!context.demoSessionId && !context.demoWhiteboardId) {
		return;
	}

	useOnboardingDemoStore.getState().saveDemoRegistration({
		demoSessionId: context.demoSessionId,
		demoWhiteboardId: context.demoWhiteboardId,
	});
}

export function getStoredDemoRegistrationContext(): DemoRegistrationContext | null {
	const { demoSessionId, demoWhiteboardId } = useOnboardingDemoStore.getState();
	if (!demoSessionId && !demoWhiteboardId) {
		return null;
	}

	return {
		demoSessionId,
		demoWhiteboardId,
	};
}

export function clearDemoRegistrationContext(): void {
	useOnboardingDemoStore.getState().clearRegistrationContext();
}
